import { useQuery } from '@tanstack/react-query';
import { OfficeReviewsService } from '../services';

export const OFFICE_REVIEW_BY_SHIFT_QUERY_KEY = 'GET_OFFICE_REVIEW_BY_SHIFT';

/**
 * The office-side review for one shift, for the Job Detail Rating tab.
 *
 * **Three outcomes, and the caller has to tell them apart** — which is the
 * whole reason this returns a flattened object rather than the query:
 *
 * | | `isPending` | `isError` | `review` |
 * | --- | --- | --- | --- |
 * | loading | `true` | `false` | `null` |
 * | failed | `false` | `true` | `null` |
 * | no review for this shift | `false` | `false` | `null` |
 * | found | `false` | `false` | entity |
 *
 * A null `review` with `isError` false means the shift has no review — the
 * service maps the endpoint's 404 to null. Rendering that as an error, or an
 * error as "no review", are both wrong in ways an admin would act on, so the
 * card must branch on `isError` before it branches on `review`.
 *
 * `retry: false` because a 404 is a legitimate answer and there is nothing to
 * retry toward; the card offers an explicit Retry for the failures that matter.
 *
 * @param {Object} params
 * @param {string} [params.jobId] - The job/shift whose review to read
 * @param {boolean} [params.enabled] - Escape hatch; the query is already
 *   disabled without a `jobId`
 */
export const useGetOfficeReviewByShift = ({ jobId, enabled = true }) => {
  const { data, error, isError, isLoading, refetch } = useQuery({
    queryKey: [OFFICE_REVIEW_BY_SHIFT_QUERY_KEY, jobId],
    queryFn: () => OfficeReviewsService.getOfficeReviewByShift({ jobId }),
    enabled: enabled && Boolean(jobId),
    retry: false,
    refetchOnWindowFocus: false,
    // The card renders its own inline failure. Without this, `queryClient`'s
    // QueryCache.onError also toasts the raw axios message, so one failure
    // reports twice — and `createTestQueryClient` installs no caches, so no
    // test would reveal it.
    meta: { skipGlobalErrorToaster: true },
  });

  return {
    /** The mapped review, or null for "no review" *and* for a failure. */
    review: data ?? null,
    // `isLoading`, not the query's `isPending`: that stays true while the query
    // is disabled, which would strand a spinner on a card with no job id.
    isPending: isLoading,
    isError,
    error,
    refetch,
  };
};
